import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Tutorial } from '../types/tutorial';

export function useTutorial(slug: string | undefined) {
  const [tutorial, setTutorial] = useState<Tutorial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTutorial() {
      if (!slug) {
        setError('Tutorial slug is required');
        setLoading(false);
        return;
      }

      try {
        // Reset state at start of fetch
        setLoading(true);
        setTutorial(null);
        setError(null);
        
        const { data, error: fetchError } = await supabase
          .from('tutorials')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .single();

        // Debug: Log raw Supabase response
        console.log('useTutorial: Supabase response for slug', slug, ':', { data, error: fetchError });

        if (fetchError) {
          // Handle "no rows found" error specifically
          if (fetchError.code === 'PGRST116') {
            setTutorial(null);
            setError('Tutorial not found');
            setLoading(false);
            return;
          }
          throw fetchError;
        }
        
        setTutorial(data);
        setLoading(false);
        
        // Debug: Log state after setting tutorial
        console.log('useTutorial: State after fetch - tutorial:', data, 'loading:', false, 'error:', null);
      } catch (err) {
        console.error('Error fetching tutorial:', err);
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        setTutorial(null);
        setLoading(false);
        
        // Debug: Log state after error
        console.log('useTutorial: State after error - tutorial:', null, 'loading:', false, 'error:', errorMessage);
      }
    }

    fetchTutorial();
  }, [slug]);

  return { tutorial, loading, error };
}